import { useCallback, useEffect, useRef } from 'react';
import { LiveInsight, SuggestedQuestion } from '../../../types/live';

export interface LiveEventBatch {
  insights: LiveInsight[];
  questions: SuggestedQuestion[];
}

// Batches high-frequency SSE arrivals (insight.updated / question.updated) into
// a single commit per window, so a generation burst causes one render instead
// of one per event. A full buffer commits early so a sustained stream still
// surfaces promptly rather than being deferred window after window.
export const useLiveEventBuffer = (
  onCommit: (batch: LiveEventBatch) => void,
  windowMs = 700,
  maxEvents = 10
) => {
  const pendingRef = useRef<LiveEventBatch>({ insights: [], questions: [] });
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onCommitRef = useRef(onCommit);
  useEffect(() => {
    onCommitRef.current = onCommit;
  }, [onCommit]);

  const flush = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const batch = pendingRef.current;
    if (batch.insights.length === 0 && batch.questions.length === 0) return;
    pendingRef.current = { insights: [], questions: [] };
    onCommitRef.current(batch);
  }, []);

  const schedule = useCallback(() => {
    const size = pendingRef.current.insights.length + pendingRef.current.questions.length;
    if (size >= maxEvents) {
      flush();
      return;
    }
    if (!timerRef.current) {
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        flush();
      }, windowMs);
    }
  }, [flush, maxEvents, windowMs]);

  const pushInsight = useCallback(
    (insight: LiveInsight) => {
      pendingRef.current.insights.push(insight);
      schedule();
    },
    [schedule]
  );

  const pushQuestion = useCallback(
    (question: SuggestedQuestion) => {
      pendingRef.current.questions.push(question);
      schedule();
    },
    [schedule]
  );

  // Drop the timer on unmount; anything still buffered has no view to land in.
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  return { pushInsight, pushQuestion, flush };
};
